import Img from "gatsby-image"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import "./youtube-video.scss"

export const getYouTubeId = url =>
  url?.match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([\w-]{11})/)?.[1] ||
  null

/**
 * YoutubeVideo - shows a thumbnail with a play button and loads the YouTube
 * player only when it is clicked, so a page with several videos stays fast.
 * Uses YouTube's own thumbnail unless a Contentful image is given. Pass a new
 * `seek` ({ seconds }) to start playing from that point, for example from a
 * transcript timestamp.
 */
const YoutubeVideo = ({ className = "", seek, thumbnail, title, url }) => {
  const [playing, setPlaying] = useState(false)
  useEffect(() => {
    if (seek) setPlaying(true)
  }, [seek])
  const id = getYouTubeId(url)
  if (!id) return null
  const start = seek?.seconds ? `&start=${Math.floor(seek.seconds)}` : ""

  return (
    <div className={`youtube-video ${className}`}>
      {playing ? (
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="youtube-video--player"
          // A new seek reloads the player at that point.
          key={start}
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0${start}`}
          title={title}
        />
      ) : (
        <button
          aria-label={`Play video: ${title}`}
          className="youtube-video--poster"
          onClick={() => setPlaying(true)}
          type="button"
        >
          {thumbnail?.fluid ? (
            <Img
              alt=""
              className="youtube-video--thumbnail"
              fluid={thumbnail.fluid}
            />
          ) : (
            <img
              alt=""
              className="youtube-video--thumbnail"
              loading="lazy"
              // The full-size thumbnail, or the standard one for videos
              // that have none.
              onError={event => {
                if (!event.currentTarget.src.includes("hqdefault")) {
                  event.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
                }
              }}
              src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`}
            />
          )}
          <span aria-hidden="true" className="youtube-video--play">
            <svg height="28" viewBox="0 0 24 24" width="28">
              <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
            </svg>
          </span>
        </button>
      )}
    </div>
  )
}

YoutubeVideo.propTypes = {
  className: PropTypes.string,
  seek: PropTypes.shape({ seconds: PropTypes.number }),
  thumbnail: PropTypes.object,
  title: PropTypes.string.isRequired,
  url: PropTypes.string,
}

export default YoutubeVideo
